import { useState } from 'react';
import { summarizeKmdSettlement, type KmdSettlementSummary } from '@progear/finance-core';
import {
  useAddKmdManualPayment,
  useDeleteKmdManualPayment,
  useSetKmdFinalized,
  useUpdateKmdManualPayment,
} from '@/hooks/useKmdSettlements';
import { formatEtDate, formatEur, type CashFlowEntry, type CashFlowRequestOptions } from '@/lib/cashFlow';
import { formatCentsForInput, parseEurInput } from '@/lib/loans';

/**
 * Operator-facing label + badge tint per finance-core settlement status
 * (`summarizeKmdSettlement().status`): the resolved source of the month's
 * money — `Prognoos` (no actual yet, tentative amber), `MAN` (a manual
 * installment is the truth), `LHV` (a bank booking is the truth). The ONE
 * place the status is NAMED; the panel only renders finance-core's decision
 * (master rule: one truth), never re-derives it from the payment rows.
 */
const KMD_STATUS_META: Record<KmdSettlementSummary['status'], { label: string; cls: string }> = {
  forecast: { label: 'Prognoos', cls: 'bg-[hsl(var(--pending))]/15 text-[hsl(var(--pending))]' },
  manual: { label: 'MAN', cls: 'bg-accent text-muted-foreground' },
  lhv: { label: 'LHV', cls: 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]' },
};

export function KmdSettlementPanel({
  entry,
  today,
  options,
}: {
  entry: CashFlowEntry;
  today: string;
  options?: CashFlowRequestOptions;
}) {
  const settlement = entry.kmdSettlement;
  const addPayment = useAddKmdManualPayment(options);
  const deletePayment = useDeleteKmdManualPayment(options);
  const updatePayment = useUpdateKmdManualPayment(options);
  const finalize = useSetKmdFinalized(options);
  const [open, setOpen] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [date, setDate] = useState(today);
  const [amountText, setAmountText] = useState('');

  if (!settlement) return null;

  const summary = summarizeKmdSettlement(settlement, entry.money.gross);
  const statusMeta = KMD_STATUS_META[summary.status];
  const amountCents = parseEurInput(amountText);
  const canAdd = amountCents !== null && date.length > 0 && !addPayment.isPending;
  const effective = settlement.payments.filter((payment) => payment.supersededByPaymentId === null);

  return (
    <section className="rounded-lg border border-border bg-card/40" data-testid="kmd-settlement-panel">
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">KMD arveldus</h3>
        {/* The ONE operator-facing name for finance-core's resolved settlement
            source (lhv > manual > forecast) — never re-derived here (master rule). */}
        <span
          data-testid="kmd-status"
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${statusMeta.cls}`}
        >
          {statusMeta.label}
        </span>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {settlement.month} · {settlement.finalized ? 'lõplik' : 'avatud'}
        </span>
      </header>

      <div className="grid grid-cols-2 gap-2 px-3 py-3 text-xs">
        <Stat label="Prognoos" value={summary.forecastCents} tentative />
        <Stat label="Tegelikud maksed" value={summary.actualCents} />
        <Stat label="Prognoosijääk" value={summary.remainingForecastCents} tentative />
        <div className="rounded border border-border/60 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Erinevus</div>
          <div className="mt-0.5 tabular-nums text-foreground">
            {summary.kmdVariance.diffCents >= 0 ? '+' : '−'}
            {formatEur(Math.abs(summary.kmdVariance.diffCents))}
            {summary.kmdVariance.diffPct !== null && (
              <span className="ml-1 text-muted-foreground">
                ({summary.kmdVariance.diffPct >= 0 ? '+' : ''}{summary.kmdVariance.diffPct}%)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-border/70 px-3 py-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Osamaksed</h4>
          <span className="text-[10px] text-muted-foreground">{effective.length} arvesse minevat makset</span>
        </div>
        {settlement.payments.length === 0 ? (
          <p className="text-xs text-muted-foreground">Tegelikke makseid pole veel seotud.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {settlement.payments.map((payment) => {
              const superseded = payment.supersededByPaymentId !== null;
              return (
                <li
                  key={payment.id}
                  className={`flex items-center gap-2 rounded border border-border/60 px-2 py-1.5 text-xs ${
                    superseded ? 'opacity-50' : ''
                  }`}
                >
                  <span className="rounded bg-accent px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                    {payment.source === 'lhv' ? 'LHV' : 'MAN'}
                  </span>
                  <span className={superseded ? 'line-through' : ''}>{formatEtDate(payment.paidOn)}</span>
                  <span className={`ml-auto tabular-nums font-medium ${superseded ? 'line-through' : ''}`}>
                    {formatEur(payment.amountCents)}
                  </span>
                  {superseded ? (
                    <span className="text-[10px] text-muted-foreground">asendatud LHV-ga</span>
                  ) : payment.source === 'manual' ? (
                    <>
                      <button
                        type="button"
                        disabled={updatePayment.isPending || deletePayment.isPending}
                        onClick={() => {
                          setEditingPaymentId(payment.id);
                          setDate(payment.paidOn);
                          setAmountText(formatCentsForInput(payment.amountCents));
                          setOpen(true);
                        }}
                        className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent disabled:opacity-50"
                      >
                        Muuda
                      </button>
                      <button
                        type="button"
                        disabled={updatePayment.isPending || deletePayment.isPending}
                        onClick={() => deletePayment.mutate(payment.id)}
                        className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent disabled:opacity-50"
                      >
                        Eemalda
                      </button>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {open ? (
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded border border-border/60 p-2">
            <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Makse kuupäev
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="h-8 rounded border border-border bg-background px-2 text-xs normal-case text-foreground"
              />
            </label>
            <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Summa
              <input
                value={amountText}
                onChange={(event) => setAmountText(event.target.value)}
                placeholder="0,00"
                aria-label="KMD makse summa"
                className="h-8 w-28 rounded border border-border bg-background px-2 text-right text-xs normal-case text-foreground"
              />
            </label>
            <button
              type="button"
              disabled={!canAdd || updatePayment.isPending}
              onClick={() => {
                const input = { paidOn: date, amountCents: amountCents! };
                if (editingPaymentId) {
                  updatePayment.mutate(
                    { paymentId: editingPaymentId, input },
                    {
                      onSuccess: () => {
                        setAmountText('');
                        setEditingPaymentId(null);
                        setOpen(false);
                      },
                    },
                  );
                } else {
                  addPayment.mutate(
                    {
                      entryId: entry.id,
                      input: { ...input, idempotencyKey: crypto.randomUUID() },
                    },
                    {
                      onSuccess: () => {
                        setAmountText('');
                        setOpen(false);
                      },
                    },
                  );
                }
              }}
              className="h-8 rounded bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-50"
            >
              {editingPaymentId ? (updatePayment.isPending ? 'Muudan…' : 'Salvesta muudatus') : addPayment.isPending ? 'Lisan…' : 'Lisa osamakse'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingPaymentId(null);
                setOpen(false);
              }}
              className="h-8 px-2 text-xs text-muted-foreground"
            >
              Loobu
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDate(today);
              setEditingPaymentId(null);
              setAmountText(
                summary.remainingForecastCents > 0 ? formatCentsForInput(summary.remainingForecastCents) : '',
              );
              setOpen(true);
            }}
            className="mt-3 rounded border border-[hsl(var(--income))]/50 px-2 py-1 text-xs font-medium text-[hsl(var(--income))] hover:bg-[hsl(var(--income))]/10"
          >
            Lisa käsitsi osamakse
          </button>
        )}

        {(addPayment.isError || updatePayment.isError || deletePayment.isError || finalize.isError) && (
          <p role="alert" className="mt-2 text-xs text-red-400">
            {addPayment.error?.message ?? updatePayment.error?.message ?? deletePayment.error?.message ?? finalize.error?.message}
          </p>
        )}
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-border/70 px-3 py-2">
        <p className="text-[10px] text-muted-foreground">
          {settlement.finalized
            ? 'Kuu on lõplik: prognoosijääki rahavoos enam ei näidata.'
            : 'Sulgemine eemaldab tasumata prognoosijäägi, kuid ei muuda algset prognoosi.'}
        </p>
        <button
          type="button"
          disabled={finalize.isPending}
          onClick={() => finalize.mutate({ entryId: entry.id, finalized: !settlement.finalized })}
          className="shrink-0 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent disabled:opacity-50"
        >
          {settlement.finalized ? 'Ava kuu uuesti' : 'Märgi kuu lõplikuks'}
        </button>
      </footer>
    </section>
  );
}

function Stat({ label, value, tentative = false }: { label: string; value: number; tentative?: boolean }) {
  return (
    <div className="rounded border border-border/60 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 tabular-nums ${tentative ? 'text-[hsl(var(--pending))]' : 'text-foreground'}`}>
        {formatEur(value)}
      </div>
    </div>
  );
}